<script setup lang="ts">
const route = useRoute();

useHead({
  title: 'Tagbox used ZapScale to Increase Product Adoption by 15%, Here’s How',
  meta: [
    {
      name: 'description',
      content:
        'Know how ZapScale enabled Tagbox to gain deeper product usage insights while empowering CSMs to focus on customer relationships. Read the full case study.',
    },
    {
      property: 'og:description',
      content:
        'Know how ZapScale enabled Tagbox to gain deeper product usage insights while empowering CSMs to focus on customer relationships. Read the full case study.',
    },
    {
      property: 'og:title',
      content: 'Tagbox used ZapScale to Increase Product Adoption by 15%, Here’s How',
    },
    {
      property: 'og:url',
      content: 'https://www.zapscale.com' + route.path,
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.zapscale.com' + route.path,
    },
  ],
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ZapScale',
  description:
    'A single-source customer success management SaaS platform with 50 pre-built playbooks, 40 out-of-the-box KPIs, and unlimited user logins.',
  url: 'https://www.zapscale.com/',
  logo: 'https://www.zapscale.com/images/Header_logo.svg',
  sameAs: [
    'https://www.facebook.com/zapscale',
    'https://www.linkedin.com/company/zapscale/',
    'https://twitter.com/ZapScale',
  ],
}));
</script>
<template>
  <div class="bg-[#EAF5FD]">
    <div class="container mx-auto">
      <div class="px-5 pb-6 pt-9 lg:py-14 2xl:px-0">
        <h1 class="mb-2 text-center font-merriweather text-xl font-bold text-[#19274D] md:text-3xl lg:text-5xl">
          How Tagbox used ZapScale to
        </h1>
        <h2 class="mb-6 text-center font-merriweather text-xl font-bold text-[#19274D] md:text-3xl lg:text-5xl">
          “Increase Product Adoption by 15%”
        </h2>
        <div class="relative mx-auto mb-24 w-4/5 md:mb-28 lg:w-fit">
          <NuxtImg
            src="/images/case-study/case-study-banner.svg"
            class="absolute top-14 md:top-28"
            width="764"
            height="425"
          />
          <NuxtImg src="/images/case-study/case-study-banner-background.svg" class="mx-auto" width="550" height="346" />
        </div>
        <div class="relative mx-auto w-fit rounded-3xl shadow-2xl">
          <div class="absolute -top-6 flex w-full items-center justify-center md:-top-9">
            <div class="flex justify-center rounded-3xl bg-[#0070C0] pt-2 max-lg:px-3 lg:h-14 lg:w-48">
              <NuxtImg
                src="/images/case-study/tagbox.svg"
                alt="tagbox"
                width="135"
                height="46"
                class="max-md:w-[100px]"
              />
            </div>
          </div>
          <div
            class="flex w-full flex-col gap-y-6 rounded-3xl bg-[#0070C0] px-5 pb-5 pt-7 md:flex-row md:items-start md:justify-between md:gap-x-4 md:p-8 lg:h-[325px] lg:items-center lg:gap-x-8 lg:px-16 lg:py-10"
          >
            <div class="h-full text-white md:w-1/2 lg:h-4/5">
              <p class="font-merriweather text-2xl font-bold md:mb-4 md:text-3xl lg:mb-8 lg:text-[35px]">
                The Challenge
              </p>
              <p class="text-3xl font-medium">
                Identification of gaps between customer health, product adoption and customer engagement due to lack of
                resources.
              </p>
            </div>
            <span class="h-[150px] w-[1px] bg-[#46B0FB] max-md:hidden lg:h-[180px]"></span>
            <div class="h-full text-white md:w-1/2 lg:h-4/5">
              <p class="font-merriweather text-2xl font-bold md:mb-4 md:text-3xl lg:mb-8 lg:text-[35px]">The Result</p>
              <div class="grid grid-cols-2 gap-x-8">
                <div class="col-span-1">
                  <p class="font-black text-[#FFC52F] lg:text-6xl">15%</p>
                  <p class="font-medium lg:text-2xl">Increase in product adoption</p>
                </div>
                <div class="col-span-1">
                  <p class="font-black text-[#FFC52F] lg:text-6xl">40%</p>
                  <p class="font-medium lg:text-2xl">
                    Increase in CSM<br class="hidden lg:block" />
                    team productivity
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p
          class="mx-auto mt-8 w-[90%] font-merriweather text-base font-normal italic text-[#0070C0] md:text-lg lg:mt-16 lg:w-full lg:text-center lg:text-2xl"
        >
          "Implementation was quick and the support very thorough. ZapScale's drag-and-drop product feature tracking is
          phenomenal! We can see exactly how our customers are using our new features without bothering the devs. The
          heatmaps make it easy to identify customer trends."
        </p>
        <p class="mt-4 text-center text-[22px] font-semibold text-[#0070C0]">Neeraj Singal</p>
        <p class="text-center text-sm font-normal text-[#0070C0]">Chief Executive Officer</p>
      </div>
    </div>
  </div>

  <div class="bg-white">
    <div class="container mx-auto">
      <div
        class="mx-auto flex flex-col px-5 py-6 text-[#19274D] max-md:gap-y-6 md:w-[90%] md:flex-row md:items-start md:gap-x-6 lg:gap-x-16 lg:px-0 lg:py-12"
      >
        <p class="md:w-1/2">
          Tagbox offers a comprehensive suite of social media solutions that transforms social content into a valuable
          asset for businesses, events, and online presence. A robust social media aggregator, enhances e-commerce by
          turning user-generated content into a dynamic storefront and empowers website owners to seamlessly integrate
          curated social feeds, fostering interactivity and authenticity. 
        </p>
        <div class="grid grid-cols-2 max-md:gap-4 md:w-1/2 md:gap-8 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-8">
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">Headquarters</p>
            <p class="text-lg font-bold lg:text-xl">California, USA</p>
          </div>
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">Industry</p>
            <p class="text-lg font-bold lg:text-xl">Social Media</p>
          </div>
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">Founded In</p>
            <p class="text-lg font-bold lg:text-xl">2018</p>
          </div>
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">Company Size</p>
            <p class="text-lg font-bold lg:text-xl">80-100</p>
          </div>
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">G2 Rating</p>
            <p class="text-lg font-bold lg:text-xl">4.9</p>
          </div>
          <div class="col-span-1">
            <p class="text-base font-normal lg:text-[17px]">CS Team Size</p>
            <p class="text-lg font-bold lg:text-xl">5</p>
          </div>
        </div>
      </div>

      <hr class="mx-auto h-[1px] w-[90%] bg-[#B1C4D2]" />

      <div class="mx-auto px-5 py-6 text-[#19274D] md:w-[90%] lg:px-0 lg:py-12">
        <h2 class="font-merriweather text-xl font-bold md:text-3xl lg:text-[35px]">Too much to do. Too little time.</h2>
        <div class="mt-3 md:mt-5 md:space-y-3 lg:mt-8 lg:space-y-6">
          <p>
            Tagbox offers a variety of products, but their customer success team of 5 struggled to keep up with a large
            number of customers. They were constantly strapped for time and lacked the information they needed to do
            their jobs effectively.
          </p>
          <p>The biggest challenge? Understanding how customers were using Tagbox's products.</p>
          <p>
            They tried different customer success tools, but none of them provided a clear view of customer needs or
            product performance. This lack of insight made it impossible to create efficient automated workflows for the
            already overloaded customer success team.
          </p>
          <p>
            Adding to the chaos, Tagbox used a separate tool stack for each product, leading to data fragmentation and
            confusion. On top of that, these tools wouldn't work together, making it nearly impossible to get a holistic
            view of their customer base.
          </p>
          <p>The result? A frustrated customer success team and potentially unhappy customers.</p>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-[#4CBF6F]">
    <div class="container mx-auto">
      <div class="px-5 py-6 lg:py-14 2xl:px-0">
        <h2 class="text-center font-merriweather text-xl font-bold text-white md:text-3xl lg:text-6xl">
          How did ZapScale help?
        </h2>
        <div class="mt-6 flex w-full flex-col gap-y-12 text-[#19274D] lg:mt-16">
          <div
            class="bottom-box flex flex-col items-center justify-between gap-x-4 gap-y-4 rounded-3xl bg-white p-4 md:px-12 md:py-12 lg:h-[501px] lg:flex-row lg:items-start lg:gap-x-10 2xl:gap-x-8"
          >
            <div class="lg:w-1/2">
              <div class="mb-4 flex items-center gap-3 max-xl:relative max-md:justify-between md:mb-6 md:gap-10">
                <div
                  class="top-box-inside flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#4CBF6F] md:h-[99px] md:w-[99px]"
                >
                  <NuxtImg
                    loading="lazy"
                    src="/images/case-study/icon-1.svg"
                    :placeholder="[60]"
                    class="h-[20px] w-[20px] md:h-[60px] md:w-[60px]"
                    width="60"
                    height="60"
                    alt="data"
                  />
                </div>
                <span
                  class="flex h-[40px] w-fit items-center font-merriweather text-lg font-bold max-xl:absolute max-md:-top-[2px] max-md:left-[52px] md:w-[334px] md:text-[35px] md:leading-[47px] md:max-xl:left-[105px]"
                  >Customer data at a glance</span
                >
              </div>
              <span class="mb-3 block w-full text-lg font-normal lg:mb-5 xl:w-[497px]"
                >The first thing the team at Tagbox did was integrate with all their customer touchpoint tools - CRM,
                ticketing, feature requests, payments and communications to bring all the data into a single view.</span
              >
              <span class="mb-3 block text-[15px] font-normal md:text-lg"
                >The integrations took a couple of weeks and solved their key challenge of different data in disparate
                systems.</span
              >
            </div>
            <NuxtImg
              loading="lazy"
              :placeholder="[430, 408]"
              src="/images/case-study/customer-data.webp"
              width="430"
              height="408"
              class=""
              alt="Customer data at a glance"
            />
          </div>
          <div
            class="bottom-box flex flex-col items-center justify-between gap-x-4 gap-y-4 rounded-3xl bg-white p-4 md:px-12 md:py-12 lg:h-[501px] lg:flex-row-reverse lg:items-start lg:gap-x-10 2xl:gap-x-8"
          >
            <div class="lg:w-1/2">
              <div class="mb-4 flex items-center gap-3 max-xl:relative max-md:justify-between md:mb-6 md:gap-10">
                <div
                  class="top-box-inside flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#4CBF6F] md:h-[99px] md:w-[99px]"
                >
                  <NuxtImg
                    loading="lazy"
                    src="/images/case-study/icon-2.svg"
                    :placeholder="[60]"
                    class="h-[20px] w-[20px] md:h-[60px] md:w-[60px]"
                    width="60"
                    height="60"
                    alt="tracking"
                  />
                </div>
                <span
                  class="flex h-[40px] w-fit items-center font-merriweather text-lg font-bold max-xl:absolute max-md:-top-[2px] max-md:left-[52px] md:w-[334px] md:text-[35px] md:leading-[47px] md:max-xl:left-[105px]"
                  >Easy feature tracking</span
                >
              </div>
              <span class="mb-3 block w-full text-lg font-normal lg:mb-5 xl:w-[497px]"
                >Next, Tagbox leveraged the on-screen feature tagging tool to map out key features and quickly went on
                to understand feature adoption and usage for their customers and users.</span
              >
              <span class="mb-3 block text-[15px] font-normal md:text-lg"
                >Identifying the gaps in feature adoption, CSMs quickly got into action and built training programs and
                engagement plans for feature adoption.<span class="font-semibold">
                  This robust understanding of feature adoption coupled with actionable insights and plans helped
                  improve feature adoption by 15% immediately.</span
                ></span
              >
            </div>
            <NuxtImg
              loading="lazy"
              :placeholder="[500, 377]"
              src="/images/case-study/easy-feature-tracking.webp"
              width="500"
              height="377"
              class=""
              alt="Easy feature tracking"
            />
          </div>
          <div
            class="bottom-box flex flex-col items-center justify-between gap-x-4 gap-y-4 rounded-3xl bg-white p-4 md:px-12 md:py-12 lg:h-[501px] lg:flex-row lg:items-end lg:gap-x-10 2xl:gap-x-8"
          >
            <div class="lg:w-1/2">
              <div class="mb-4 flex items-center gap-3 max-xl:relative max-md:justify-between md:mb-6 md:gap-10">
                <div
                  class="top-box-inside flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#4CBF6F] md:h-[99px] md:w-[99px]"
                >
                  <NuxtImg
                    loading="lazy"
                    src="/images/case-study/icon-3.svg"
                    :placeholder="[60]"
                    class="h-[20px] w-[20px] md:h-[60px] md:w-[60px]"
                    width="60"
                    height="60"
                    alt=""
                  />
                </div>
                <span
                  class="flex h-[40px] w-fit items-center font-merriweather text-lg font-bold max-xl:absolute max-md:-top-[2px] max-md:left-[52px] md:w-[450px] md:text-[35px] md:leading-[47px] md:max-xl:left-[105px]"
                  >Automation for improved productivity</span
                >
              </div>
              <span class="mb-3 block w-full text-lg font-normal lg:mb-5 xl:w-[497px]"
                >Tagbox leveraged pre-built Playbooks on ZapScale, for seamless initiation of workflows in response to
                customer concerns. This helped CSMs stay on top of all customer red flags and solve them.</span
              >
              <span class="mb-3 block text-[15px] font-normal md:text-lg"
                >Additionally, ZapScale's Success Plans enabled the CS team to follow internal SOPs, facilitating better
                coordination and more awareness of customer outcomes and progress.
                <span class="font-semibold"
                  >Tagbox's CS team saw a 40% productivity jump, thanks to clearer data and automated workflows
                  triggered by those insights.</span
                ></span
              >
            </div>
            <NuxtImg
              loading="lazy"
              :placeholder="[480, 340]"
              src="/images/case-study/automation.webp"
              width="480"
              height="340"
              class=""
              alt="Automation for improved productivity"
            />
          </div>
        </div>

        <p
          class="mx-auto mt-4 w-4/5 text-center text-xl font-normal text-white md:mt-6 md:text-2xl lg:mt-16 lg:w-full lg:text-3xl"
        >
          Overall, ZapScale aided Tagbox in achieving deeper insights into their product usage and adoption at the same
          time freed CSMs to focus on customers.
        </p>
      </div>
    </div>
  </div>

  <div class="bg-[#FFC52F]">
    <div class="container mx-auto">
      <div class="px-5 py-6 md:px-14">
        <span
          class="mx-auto mb-6 block text-center font-merriweather text-3xl font-bold text-black md:mb-8 xl:text-[35px] xl:leading-[50px]"
        >
          Simplify your customer success
          <span class="block"> operations with the easiest customer success platform. </span>
        </span>
        <NuxtLink
          to="/demo/#book-a-demo"
          class="mx-auto block h-[41px] w-[179px] rounded-full bg-[#FF395B] px-6 py-2 text-base font-semibold text-white hover:border hover:border-[#FF395B] hover:bg-white hover:text-[#FF395B]"
        >
          <span class="flex items-center justify-between">
            Get Started
            <Icon name="solar:arrow-right-linear" />
          </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.top-box-inside {
  box-shadow: inset 0 7px 9px -7px rgba(0, 0, 0, 0.4);
}

.bottom-box {
  box-shadow: 0px 32px 10px -20px rgba(0, 0, 0, 0.1);
}
</style>
